import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Dumbbell, Edit2, Trash2, Save, X, ChevronDown, Filter, Users,
  GripVertical, Copy, Layers, ChevronRight, Clock, PlayCircle, Youtube
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

// Card de treino salvo
const WorkoutCard = ({ workout, onEdit, onDelete, onView, onDuplicate }) => (
  <motion.div
    className="bg-zinc-950/50 border border-white/5 rounded-xl p-4 hover:border-neon-purple/30 transition-all"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    data-testid="saved-workout-card"
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <h4 className="font-bold text-white">{workout.name}</h4>
        {workout.category && (
          <span className="inline-block text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full mt-1">
            {workout.category}
          </span>
        )}
      </div>
      <div className="flex gap-1">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDuplicate} 
          className="text-zinc-400 hover:text-neon-lime p-2"
          title="Duplicar treino"
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onEdit} 
          className="text-zinc-400 hover:text-neon-purple p-2"
          title="Editar"
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDelete} 
          className="text-zinc-400 hover:text-red-500 p-2"
          title="Excluir"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
    {workout.description && <p className="text-zinc-500 text-sm mb-3 line-clamp-2">{workout.description}</p>}
    
    {/* Contagem de exercícios e super séries */}
    <div className="flex items-center gap-3 text-sm text-zinc-400 mb-3">
      <span className="flex items-center gap-1">
        <Dumbbell className="w-4 h-4" />
        {workout.exercises?.filter(e => !e.is_super_series).length || 0} exercício(s)
      </span>
      {workout.exercises?.some(e => e.is_super_series) && (
        <span className="flex items-center gap-1 text-neon-lime">
          <Layers className="w-4 h-4" />
          {workout.exercises.filter(e => e.is_super_series).length} super série(s)
        </span>
      )}
    </div>
    
    <Button 
      onClick={onView} 
      variant="outline" 
      className="w-full border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10 text-sm"
      data-testid="view-workout-details"
    >
      Ver Detalhes
    </Button>
  </motion.div>
);

// Linha de exercício com drag-and-drop
const DraggableExerciseRow = ({ exercise, index, onUpdate, onRemove }) => {
  const isSuperSeries = exercise.is_super_series;
  
  // Função para parar propagação e permitir digitação nos inputs
  const stopPropagation = (e) => {
    e.stopPropagation();
  };
  
  return (
    <Reorder.Item
      value={exercise}
      className={`
        border rounded-lg p-3 cursor-grab active:cursor-grabbing
        ${isSuperSeries 
          ? 'bg-gradient-to-r from-neon-purple/10 to-neon-lime/5 border-neon-purple/30' 
          : 'bg-zinc-900 border-zinc-700'
        }
      `}
      data-testid={`exercise-row-${index}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-zinc-500 hover:text-zinc-300" />
          <span className={`text-sm font-bold ${isSuperSeries ? 'text-neon-purple' : 'text-zinc-500'}`}>
            {index + 1}
          </span>
        </div>
        
        {isSuperSeries ? (
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-neon-purple" />
              <span className="font-bold text-white">{exercise.super_series_name || exercise.name}</span>
              <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                {exercise.super_series_method}
              </span>
            </div>
            <div className="space-y-1 ml-6">
              {exercise.super_series_exercises?.map((subEx, subIdx) => (
                <div key={subIdx} className="flex items-center gap-2 text-sm text-zinc-400">
                  <ChevronRight className="w-3 h-3" />
                  <span>{subEx.name}</span>
                  {subEx.execution_details && (
                    <span className="text-zinc-500">({subEx.execution_details})</span>
                  )}
                  {subEx.youtube_url && (
                    <a 
                      href={subEx.youtube_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-red-500 hover:text-red-400"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
            {exercise.rest_between_exercises && (
              <div className="flex items-center gap-1 mt-2 ml-6 text-xs text-zinc-500">
                <Clock className="w-3 h-3" />
                Entre exercícios: {exercise.rest_between_exercises}
              </div>
            )}
            {exercise.rest_after_series && (
              <div className="flex items-center gap-1 ml-6 text-xs text-zinc-500">
                <Clock className="w-3 h-3" />
                Após série: {exercise.rest_after_series}
              </div>
            )}
            <div className="flex justify-end mt-2">
              <Button 
                type="button" 
                onClick={onRemove} 
                variant="ghost" 
                size="sm" 
                className="text-red-400 hover:text-red-300 p-2"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2" onPointerDown={stopPropagation}>
            <Input
              value={exercise.name}
              onChange={(e) => onUpdate('name', e.target.value)}
              placeholder="Nome"
              className="bg-zinc-800 border-zinc-600 text-white text-sm h-9 cursor-text"
              onPointerDown={stopPropagation}
            />
            <Input
              value={exercise.youtube_url || ''}
              onChange={(e) => onUpdate('youtube_url', e.target.value)}
              placeholder="YouTube URL"
              className="bg-zinc-800 border-zinc-600 text-white text-sm h-9 cursor-text"
              onPointerDown={stopPropagation}
            />
            <Input
              value={exercise.execution_details || ''}
              onChange={(e) => onUpdate('execution_details', e.target.value)}
              placeholder="Ex: 3x12, 4x10"
              className="bg-zinc-800 border-zinc-600 text-white text-sm h-9 cursor-text"
              onPointerDown={stopPropagation}
            />
            <div className="flex gap-2">
              <Input
                value={exercise.instructions || ''}
                onChange={(e) => onUpdate('instructions', e.target.value)}
                placeholder="Instruções"
                className="flex-1 bg-zinc-800 border-zinc-600 text-white text-sm h-9 cursor-text"
                onPointerDown={stopPropagation}
              />
              <Button 
                type="button" 
                onClick={onRemove} 
                variant="ghost" 
                size="sm" 
                className="text-red-400 hover:text-red-300 p-2"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Reorder.Item>
  );
};

// Modal de seleção de Super Série
const SuperSeriesSelector = ({ superSeries, onSelect, onClose }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
    <motion.div 
      className="bg-zinc-900 border border-neon-purple/30 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Layers className="w-6 h-6 text-neon-purple" />
          <h3 className="font-bold text-xl text-white">Adicionar Super Série</h3>
        </div>
        <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </Button>
      </div>
      
      {superSeries.length === 0 ? (
        <div className="text-center py-8">
          <Layers className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhuma super série cadastrada</p>
          <p className="text-zinc-600 text-sm">Crie super séries na aba "Super Série" do painel admin</p>
        </div>
      ) : (
        <div className="space-y-3">
          {superSeries.map((ss) => (
            <div 
              key={ss.id}
              className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 hover:border-neon-purple/50 cursor-pointer transition-all"
              onClick={() => onSelect(ss)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-white">{ss.name}</h4>
                  <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                    {ss.method_name}
                  </span>
                </div>
                <span className="text-sm text-zinc-500">{ss.exercises?.length || 0} exercícios</span>
              </div>
              <div className="space-y-1">
                {ss.exercises?.slice(0, 3).map((ex, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-zinc-400">
                    <ChevronRight className="w-3 h-3" />
                    <span>{ex.name}</span>
                    {ex.youtube_url && (
                      <Youtube className="w-3 h-3 text-red-500" />
                    )}
                  </div>
                ))}
                {ss.exercises?.length > 3 && (
                  <p className="text-xs text-zinc-500 ml-5">+{ss.exercises.length - 3} mais...</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  </div>
);

// Modal de detalhes do treino
const WorkoutDetailsModal = ({ workout, onClose, onEdit, onApply }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
    <motion.div 
      className="bg-zinc-900 border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" 
      initial={{ opacity: 0, scale: 0.9 }} 
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-xl text-white">{workout.name}</h3>
          {workout.category && (
            <span className="inline-block text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full mt-1">
              {workout.category}
            </span>
          )}
        </div>
        <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
          <X className="w-5 h-5" />
        </Button>
      </div>
      
      {workout.description && <p className="text-zinc-400 mb-4">{workout.description}</p>}
      
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-zinc-400 uppercase">
          Exercícios ({workout.exercises?.length || 0})
        </h4>
        {workout.exercises?.map((ex, idx) => (
          <div 
            key={ex.id || idx} 
            className={`
              border rounded-lg p-3
              ${ex.is_super_series 
                ? 'bg-gradient-to-r from-neon-purple/10 to-neon-lime/5 border-neon-purple/30' 
                : 'bg-zinc-950 border-zinc-800'
              }
            `}
          >
            {ex.is_super_series ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-neon-purple font-bold">{idx + 1}</span>
                  <Layers className="w-4 h-4 text-neon-purple" />
                  <span className="font-bold text-white">{ex.super_series_name || ex.name}</span>
                  <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">
                    {ex.super_series_method}
                  </span>
                </div>
                <div className="space-y-2 ml-6">
                  {ex.super_series_exercises?.map((subEx, subIdx) => (
                    <div key={subIdx} className="flex items-center gap-2 text-sm">
                      <ChevronRight className="w-3 h-3 text-zinc-500" />
                      <span className="text-white">{subEx.name}</span>
                      {subEx.execution_details && (
                        <span className="text-zinc-500">({subEx.execution_details})</span>
                      )}
                      {subEx.youtube_url && (
                        <a 
                          href={subEx.youtube_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-red-500 hover:text-red-400"
                        >
                          <Youtube className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                {(ex.rest_between_exercises || ex.rest_after_series) && (
                  <div className="flex gap-4 mt-2 ml-6 text-xs text-zinc-500">
                    {ex.rest_between_exercises && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Entre: {ex.rest_between_exercises}
                      </span>
                    )}
                    {ex.rest_after_series && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Após: {ex.rest_after_series}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="text-neon-purple font-bold">{idx + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h5 className="font-bold text-white">{ex.name}</h5>
                    {ex.execution_details && (
                      <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">
                        {ex.execution_details}
                      </span>
                    )}
                    {ex.youtube_url && (
                      <PlayCircle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  {ex.instructions && <p className="text-sm text-zinc-400 mt-1">{ex.instructions}</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="flex gap-3 mt-6">
        <Button onClick={onEdit} className="flex-1 bg-neon-purple text-white font-bold">
          <Edit2 className="w-5 h-5 mr-2" />Editar
        </Button>
        <Button onClick={onApply} className="flex-1 bg-neon-lime text-black font-bold">
          <Users className="w-5 h-5 mr-2" />Usar com Aluno
        </Button>
        <Button onClick={onClose} className="bg-zinc-800 text-white">Fechar</Button>
      </div>
    </motion.div>
  </div>
);

// Componente principal
export const SavedWorkoutsManager = ({ savedWorkouts, savedWorkoutCategories, exerciseLibrary, superSeries = [], onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const [showSuperSeriesSelector, setShowSuperSeriesSelector] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    category: '', 
    exercises: [] 
  });

  // Handler para reordenar exercícios via drag-and-drop
  const handleReorder = useCallback((newOrder) => {
    setFormData(prev => ({
      ...prev,
      exercises: newOrder.map((ex, idx) => ({ ...ex, order: idx }))
    }));
  }, []);

  // Salvar (atualizar existente)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do treino é obrigatório');
      return;
    }
    
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/saved-workouts/${editingId}`, formData);
        toast.success('Treino atualizado!');
      } else {
        await axios.post(`${API}/admin/saved-workouts`, formData);
        toast.success('Treino salvo com sucesso!');
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error('Erro ao salvar treino');
    }
  };

  // Salvar como novo (duplicar e salvar)
  const handleSaveAsNew = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do treino é obrigatório');
      return;
    }
    
    try {
      // Sempre cria um novo treino, mesmo se estiver editando
      const newData = {
        ...formData,
        name: editingId ? formData.name : `${formData.name} (Novo)`
      };
      await axios.post(`${API}/admin/saved-workouts`, newData);
      toast.success('Novo treino criado com sucesso!');
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error('Erro ao criar treino');
    }
  };

  // Duplicar treino existente
  const handleDuplicate = async (workoutId) => {
    try {
      await axios.post(`${API}/admin/saved-workouts/${workoutId}/duplicate`);
      toast.success('Treino duplicado com sucesso!');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao duplicar treino');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir o treino "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/saved-workouts/${id}`);
      toast.success('Treino excluído');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '', category: '', exercises: [] });
  };

  // Adicionar exercício normal
  const addExercise = () => {
    const newId = `temp-${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, { 
        id: newId,
        name: '', 
        youtube_url: '', 
        instructions: '', 
        execution_details: '',
        order: prev.exercises.length,
        is_super_series: false
      }]
    }));
  };

  // Adicionar da biblioteca
  const addFromLibrary = (ex) => {
    const newId = `temp-${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, { 
        id: newId,
        name: ex.name, 
        youtube_url: ex.youtube_url || '', 
        instructions: ex.instructions || '', 
        execution_details: ex.execution_details || '',
        order: prev.exercises.length,
        is_super_series: false
      }]
    }));
  };

  // Adicionar super série
  const addSuperSeries = (ss) => {
    const newId = `temp-ss-${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, {
        id: newId,
        name: ss.name,
        is_super_series: true,
        super_series_id: ss.id,
        super_series_name: ss.name,
        super_series_method: ss.method_name,
        super_series_exercises: ss.exercises?.map(ex => ({
          name: ex.name,
          youtube_url: ex.youtube_url,
          instructions: ex.instructions,
          execution_details: ex.execution_details
        })),
        rest_between_exercises: ss.rest_between_exercises,
        rest_after_series: ss.rest_after_series,
        order: prev.exercises.length
      }]
    }));
    setShowSuperSeriesSelector(false);
    toast.success(`Super série "${ss.name}" adicionada!`);
  };

  const updateExercise = (idx, field, value) => {
    setFormData(prev => {
      const newExercises = [...prev.exercises];
      newExercises[idx] = { ...newExercises[idx], [field]: value };
      return { ...prev, exercises: newExercises };
    });
  };

  const removeExercise = (idx) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== idx)
    }));
  };

  const openEdit = (workout) => {
    setFormData({ 
      name: workout.name, 
      description: workout.description || '', 
      category: workout.category || '', 
      exercises: workout.exercises || [] 
    });
    setEditingId(workout.id);
    setShowForm(true);
    setViewingWorkout(null);
  };

  const filteredWorkouts = savedWorkouts.filter((w) => {
    const matchSearch = w.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = !categoryFilter || w.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="bg-zinc-900/50 border border-neon-purple/30 rounded-2xl p-6" data-testid="saved-workouts-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Save className="w-8 h-8 text-neon-purple" />
          <div>
            <h3 className="font-primary font-bold text-xl text-white">Treinos Salvos</h3>
            <p className="text-zinc-500 text-sm">Treinos pré-montados para reutilizar</p>
          </div>
        </div>
        <Button 
          onClick={() => { 
            setShowForm(true); 
            setEditingId(null); 
            setFormData({ name: '', description: '', category: '', exercises: [] }); 
          }} 
          className="bg-neon-purple text-white font-black uppercase hover:bg-neon-purple/80"
          data-testid="new-workout-button"
        >
          <Plus className="w-5 h-5 mr-2" />Novo Treino
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input 
            type="text" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            placeholder="Buscar treino..." 
            className="pl-10 bg-zinc-950/50 border-zinc-800 text-white h-10" 
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)} 
            className="pl-10 pr-8 h-10 bg-zinc-950/50 border border-zinc-800 rounded-lg text-white min-w-[180px]"
          >
            <option value="">Todas categorias</option>
            {savedWorkoutCategories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        </div>
      </div>

      {/* Formulário de criação/edição */}
      <AnimatePresence>
        {showForm && (
          <motion.div 
            className="bg-zinc-950/50 border border-neon-purple/30 rounded-xl p-5 mb-4" 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <h4 className="font-bold text-white text-lg mb-4">
              {editingId ? 'Editar Treino' : 'Novo Treino'}
            </h4>
            
            <div className="space-y-4">
              {/* Campos básicos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Nome *</label>
                  <Input 
                    type="text" 
                    value={formData.name} 
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
                    placeholder="Ex: Treino A - Peito e Tríceps" 
                    className="bg-zinc-900 border-zinc-700 text-white" 
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Categoria</label>
                  <Input 
                    type="text" 
                    value={formData.category} 
                    onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))} 
                    placeholder="Ex: Peito, Costas, Full Body" 
                    className="bg-zinc-900 border-zinc-700 text-white" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Descrição</label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} 
                  placeholder="Descrição do treino..." 
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-lg p-3 min-h-[60px]" 
                />
              </div>

              {/* Exercícios com drag-and-drop */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm text-zinc-400">
                    Exercícios ({formData.exercises.length})
                    <span className="text-xs text-zinc-600 ml-2">Arraste para reordenar</span>
                  </label>
                  <div className="flex gap-2">
                    {superSeries && superSeries.length > 0 && (
                      <Button 
                        type="button" 
                        onClick={() => setShowSuperSeriesSelector(true)}
                        size="sm" 
                        className="bg-neon-purple/20 text-neon-purple border border-neon-purple/30 hover:bg-neon-purple/30 text-sm"
                      >
                        <Layers className="w-4 h-4 mr-1" />Super Série
                      </Button>
                    )}
                    <select 
                      onChange={(e) => { 
                        if (e.target.value) { 
                          const ex = exerciseLibrary.find(x => x.id === e.target.value); 
                          if (ex) addFromLibrary(ex); 
                          e.target.value = ''; 
                        } 
                      }} 
                      className="h-8 px-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                    >
                      <option value="">+ Da Biblioteca</option>
                      {exerciseLibrary.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                    </select>
                    <Button 
                      type="button" 
                      onClick={addExercise} 
                      size="sm" 
                      className="bg-zinc-700 text-white text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1" />Novo
                    </Button>
                  </div>
                </div>
                
                {formData.exercises.length === 0 ? (
                  <p className="text-zinc-600 text-center py-4 border border-dashed border-zinc-700 rounded-lg">
                    Adicione exercícios ou super séries
                  </p>
                ) : (
                  <Reorder.Group 
                    axis="y" 
                    values={formData.exercises} 
                    onReorder={handleReorder}
                    className="space-y-2"
                  >
                    {formData.exercises.map((ex, idx) => (
                      <DraggableExerciseRow 
                        key={ex.id || idx} 
                        exercise={ex} 
                        index={idx} 
                        onUpdate={(f, v) => updateExercise(idx, f, v)} 
                        onRemove={() => removeExercise(idx)} 
                      />
                    ))}
                  </Reorder.Group>
                )}
              </div>

              {/* Botões de ação */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Button 
                  type="button" 
                  onClick={handleSave}
                  className="flex-1 min-w-[150px] bg-neon-purple text-white font-bold"
                  data-testid="save-workout-button"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {editingId ? 'Salvar Alterações' : 'Salvar Treino'}
                </Button>
                
                {editingId && (
                  <Button 
                    type="button" 
                    onClick={handleSaveAsNew}
                    className="flex-1 min-w-[150px] bg-neon-lime text-black font-bold"
                    data-testid="save-as-new-button"
                  >
                    <Copy className="w-5 h-5 mr-2" />Salvar como Novo
                  </Button>
                )}
                
                <Button 
                  type="button" 
                  onClick={resetForm} 
                  className="bg-zinc-800 text-white"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de treinos */}
      {filteredWorkouts.length === 0 ? (
        <div className="text-center py-8">
          <Save className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">
            {savedWorkouts.length === 0 ? 'Nenhum treino salvo ainda' : 'Nenhum treino encontrado'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWorkouts.map((w) => (
            <WorkoutCard 
              key={w.id} 
              workout={w} 
              onEdit={() => openEdit(w)} 
              onDelete={() => handleDelete(w.id, w.name)} 
              onView={() => setViewingWorkout(w)}
              onDuplicate={() => handleDuplicate(w.id)}
            />
          ))}
        </div>
      )}

      {/* Modal de detalhes */}
      {viewingWorkout && (
        <WorkoutDetailsModal
          workout={viewingWorkout}
          onClose={() => setViewingWorkout(null)}
          onEdit={() => openEdit(viewingWorkout)}
          onApply={() => {
            navigate('/admin/users-workouts');
            setViewingWorkout(null);
          }}
        />
      )}

      {/* Modal de seleção de super série */}
      {showSuperSeriesSelector && (
        <SuperSeriesSelector
          superSeries={superSeries}
          onSelect={addSuperSeries}
          onClose={() => setShowSuperSeriesSelector(false)}
        />
      )}
    </div>
  );
};
